import { z } from 'zod';

/**
 * consumer-web's Square Apple Pay page (`/square-apple-pay`), which consumer-native shows in a
 * WebView at checkout on iOS. The page shows Apple Pay's button for one order: Apple Pay's sheet
 * only opens in a tap on the page itself, and only on a hostname Square verified for the seller
 * (`consumerNative.activateApplePay`).
 *
 * The page takes `applicationId`, `locationId`, `amount` (the order total, in cents) and `label`
 * (the venue's name, shown beside the total) in its query string. WKWebView only allows Apple Pay
 * on a page the app never runs a script in (react-native-webview's `enableApplePay`), so the app
 * can't call into the page: it loads it again for a new total. The page talks to the app only
 * through these messages (`window.webkit.messageHandlers.ReactNativeWebView.postMessage`, JSON),
 * and the app pays with the nonce itself (`consumerNative.submitOrder`), so the page needs no
 * sign-in.
 *
 * Frozen once shipped: app builds read these messages, and the page deploys without them.
 */
export const squareApplePayMessageSchema = z.discriminatedUnion('type', [
  /** Apple Pay's button is showing and opens its sheet. */
  z.object({ type: z.literal('ready') }),
  /** Apple Pay won't start here, and why: the device has none, or Square refused it. */
  z.object({ type: z.literal('unavailable'), errorMessage: z.string() }),
  /** The customer paid in Apple Pay's sheet: the nonce, for the server to charge once. */
  z.object({ type: z.literal('tokenized'), squareCardNonce: z.string() }),
  /** Square made no nonce, with its reason. Not sent when the customer closes the sheet. */
  z.object({ type: z.literal('tokenizeFailed'), errorMessage: z.string() }),
]);
export type SquareApplePayMessage = z.output<typeof squareApplePayMessageSchema>;
